// RecordDetails.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom'; 
// Ant Design components
import { Table, Tabs, Button, Spin, Space } from 'antd';
// Ant Design Icons
import { ArrowLeftOutlined, DownloadOutlined, CheckCircleOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import styles from './styles/RecordDetails.module.css'; // Ensure CSS follows vanilla black and white theme

// Custom fetch utility (replace with your actual customFetch or API call function)
import customFetch from "../apicall/api"; 


// --- API Wrapper Functions ---

/**
 * Fetches initial batch details, including the list of files/tabs.
 */
const fetchBatchDetails = async (uploadRequestId, effective_designation_id) => {
  try {
    const params = new URLSearchParams({
      upload_request_id: uploadRequestId,
      effective_designation_id: effective_designation_id
    });

    // const response = await fetch(`http://localhost:5000/bulk-file-upload/list/files?${params.toString()}`, {
    //   method: "GET",
    //   headers: {
    //     "api-token": localStorage.getItem("api_token"),
    //   },
    // });

    const response = await customFetch(
      process.env.REACT_APP_URL + `/bulk-file-upload/list/files?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "api-token": localStorage.getItem("api_token"),
        },
      }
    );

    if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
    }

    const data = await response.json();
    
    if (data.status === 1 && data.data && data.data.length > 0) {
        // ✅ The batch object is the first element in the 'data' array
        const batchItem = data.data[0]; 
        
        // ✅ Restructure/rename the key: 'files_data' (from API) to 'files' (for component logic)
        return {
            ...batchItem,
            files: batchItem.files_data // Rename for consistency
        };
    }
    
    // If no data found, throw a specific error
    throw new Error("Batch details not found for the given ID.");

  } catch (error) {
    console.error("API error fetching batch details:", error);
    // Returning null indicates a failure to load the batch
    return null; 
  }
};

/**
 * Fetches paginated and sorted records for a specific file index.
 * Maps directly to your Python API call.
 */
const fetchFileRecords = async ({ uploadRequestId, fileIndex, page, pageSize, sortBy, sortOrder }) => {
  // const url = new URL(`${FLASK_API_URL}/list/records`);
  // const url = new URL(`http://localhost:5000/bulk-file-upload/list/records`);
  const url = new URL(process.env.REACT_APP_URL + `/bulk-file-upload/list/records`);
  url.searchParams.append('upload_request_id', uploadRequestId);
  url.searchParams.append('file_index', fileIndex);
  url.searchParams.append('page', page);
  url.searchParams.append('page_size', pageSize);
  if (sortBy) url.searchParams.append('sort_by', sortBy);
  if (sortOrder) url.searchParams.append('sort_order', sortOrder);

  try {
    // const response = await fetch(url.toString(), { method: 'GET', headers:{"api-token": localStorage.getItem("api_token")} });

    const response = await customFetch(
      url.toString(),
      {
        method: "GET",
        headers: {
          "api-token": localStorage.getItem("api_token"),
        },
      }
    );

    const data = await response.json();

    if (data.status === 1) {
      // ✅ Restructure data: Flatten nested 'record_details' and add 'key'
      const recordsWithKey = (data.data || []).map((item, index) => ({
          ...item.record_details, // Flatten fields like loan_id, name, etc.
          _id: item._id,          // Keep the MongoDB ID
          status: item.status,    // Keep the status
          key: item._id || `${fileIndex}-${item.record_index}`, // Key for AntD Table
      }));
      return { 
        records: recordsWithKey, 
        pagination: { 
          current: data.page, 
          pageSize: data.page_size, 
          total: data.total_count 
        } 
      };
    }
    throw new Error(data.message || "Failed to fetch file records.");
  } catch (error) {
    console.error("API error fetching file records:", error);
    return { records: [], pagination: { current: 1, pageSize: 10, total: 0 } };
  }
};

// --- Helper Functions ---

const formatColumnHeader = (key) => {
    return key
        .replace(/([A-Z])/g, ' $1') 
        .replace(/_/g, ' ')           
        .replace(/^./, str => str.toUpperCase()) 
        .trim();
};


const RecordDetails = () => {
  // Extract the ID from the URL parameter (e.g., from /record-history/SCRUB-251125-0002)
  const { uploadRequestId } = useParams(); 
  const history = useHistory();
  
  const [batchData, setBatchData] = useState(null);
  const [activeFileIndex, setActiveFileIndex] = useState(null); // The file_index (e.g., '0', '1')
  
  const [records, setRecords] = useState([]);
  const [loadingBatch, setLoadingBatch] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [sort, setSort] = useState({ sortBy: null, sortOrder: 'desc' });
  const location = useLocation();
  const { api_token, effective_designation_id } = location.state || {};


  // --- 1. Load Batch Details (To get Tabs/Files) ---
  useEffect(() => {
    const loadDetails = async () => {
      setLoadingBatch(true);
      // NOTE: Use batchId extracted from useParams
      const batch = await fetchBatchDetails(uploadRequestId, effective_designation_id); 
      
      if (batch) {
        setBatchData(batch);
        // The structure is now: batch.files (which we renamed from files_data)
        if (batch.files && batch.files.length > 0) {
          // Set the active tab to the index of the first file (e.g., '0')
          setActiveFileIndex(String(batch.files[0].file_index));
        }
      } else {
        // Handle failure to load batch
        // NOTE: Changed destination path based on your code
        history.push({
          pathname:'/bulk-file-upload/upload-history',
          state: { api_token: localStorage.getItem("api_token"), effective_designation_id: effective_designation_id }
        });
      }
      setLoadingBatch(false);
    };
    loadDetails();
  }, [uploadRequestId, history]); // Dependencies remain correct


  // --- 2. Load File Records (On Tab/Page/Sort Change) ---
  const loadRecords = useCallback(async ({ page = 1, pageSize = 10, sortBy = null, sortOrder = 'desc' }) => {
    if (!uploadRequestId || activeFileIndex === null) return;

    setLoadingRecords(true);

    const result = await fetchFileRecords({
        uploadRequestId: uploadRequestId,
        fileIndex: activeFileIndex, // Use the active index
        page,
        pageSize,
        sortBy,
        sortOrder
    });
    
    setRecords(result.records);
    setPagination(result.pagination);
    setLoadingRecords(false);
  }, [uploadRequestId, activeFileIndex]);


  // Effect to trigger loadRecords when relevant state changes
  useEffect(() => {
    // This effect runs on initial tab set, tab change, pagination change, or sort change
    if (activeFileIndex !== null) {
      loadRecords({
        page: pagination.current,
        pageSize: pagination.pageSize,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder
      });
    }
  }, [activeFileIndex, pagination.current, pagination.pageSize, sort.sortBy, sort.sortOrder, loadRecords]);


  // --- Handlers for User Interactions ---

  const handleDownload = async (file_process_type, fullName) => {
    console.log("file download clicked with values", file_process_type, fullName)
    
    const payload = {
            file_process_type: file_process_type,
            filename: fullName,
        };

    // const response = await fetch(`http://localhost:5000/bulk-file-upload/generate/download/urls`, 
    // { method: 'POST', 
    //   headers:{
    //     "api-token": localStorage.getItem("api_token"),
    //     "Content-Type": "application/json",
    //   },
    //   body: JSON.stringify(payload),
    // });

    const response = await customFetch(
      process.env.REACT_APP_URL + `/bulk-file-upload/generate/download/urls`,
      {
        method: "POST",
        headers: {
          "api-token": localStorage.getItem("api_token"),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      }
    );

    if (response.ok) {
        const { download_url } = await response.json();
        
        // 2. Use the signed URL to trigger the download
        const link = document.createElement('a');
        link.href = download_url;
        // Optionally set the suggested download filename
        // link.download = blobName; 
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log("response for download - ", download_url)
      } else {
        console.error("Failed to get signed download URL.");
    }

  };

  const handleTabChange = (newFileIndex) => {
    setActiveFileIndex(newFileIndex);
    // Reset pagination and sorting when switching tabs
    setPagination(prev => ({ ...prev, current: 1 }));
    setSort({ sortBy: null, sortOrder: 'desc' });
  };
  
  const handlePageChange = (page) => {
    // Only update page/pageSize state, the useEffect above will trigger the load
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handleSortChange = (key) => {
      // old sorting acs, dsc, null
      // const newSortOrder = sort.sortBy === key && sort.sortOrder === 'asc' ? 'desc' : 
      //                     sort.sortBy === key && sort.sortOrder === 'desc' ? null : 'asc';
      
      // const newSortBy = newSortOrder ? key : null;

      // new sorting asc, dsc
      let newSortOrder;
    
      if (sort.sortBy === key) {
          newSortOrder = sort.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
          newSortOrder = 'asc';
      }
      const newSortBy = key;
      
      setSort({ sortBy: newSortBy, sortOrder: newSortOrder });
      setPagination(prev => ({ ...prev, current: 1 })); // Reset to page 1 on sort change
  }

  const getSortIndicator = (column) => {
    if (sort.sortBy !== column) {
      // Use the styled placeholder to maintain alignment for non-sorted columns
      return <span className={styles.sortPlaceholder}></span>; 
    }
    
    // Use Unicode triangles (▲ and ▼)
    const arrow = sort.sortOrder === 'asc' ? '▲' : '▼';
    return <span className={styles.sortIndicator}>{arrow}</span>;
  };

  const handleBack = () => {
    history.push({
      pathname:'/bulk-file-upload/upload-history',
      state: { api_token: localStorage.getItem("api_token"), effective_designation_id: effective_designation_id }
    }); 
  };


  // --- Memoized Data for Ant Design Table ---

  // Generate AntD columns based on the *first* record's keys
  const tableColumns = useMemo(() => {
    if (records.length === 0) return [];
    
    // Get all keys from the first record (assuming consistent schema)
    const keys = Object.keys(records[0]).filter(key => key !== 'key'); // Exclude 'key' used by Antd

    return keys.map((key) => ({
      title: (
        // <Space onClick={() => handleSortChange(key)} className={styles.sortableHeader}>
        //   {formatColumnHeader(key)}
        //   {key === sort.sortBy ? (
        //     sort.sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />
        //   ) : (
        //     <span className={styles.sortPlaceholder}>↕</span> 
        //   )}
        // </Space>
        <div onClick={() => handleSortChange(key)} className={styles.sortableHeaderContent}>
          {formatColumnHeader(key)}
          {getSortIndicator(key)}
        </div>
      ),
      dataIndex: key,
      key: key,
      // sorter: true, // Mark as sorter for styling, but logic is handled by Space onClick
    }));
  }, [records, sort, getSortIndicator]);


  // Memoize Tab Items for Ant Design Tabs
  const tabItems = useMemo(() => {
    if (!batchData || !batchData.files) return [];

    const totalPages = Math.ceil(pagination.total / pagination.pageSize); 
    const startEntry = ((pagination.current - 1) * pagination.pageSize) + 1;
    const endEntry = Math.min(pagination.current * pagination.pageSize, pagination.total);
    
    // Sort files by file_index to ensure correct tab order
    const sortedFiles = batchData.files.sort((a, b) => a.file_index - b.file_index);

    return sortedFiles.map((file) => {
        const tabKey = String(file.file_index); 
        const fullName = file.system_generated_filename;
        const firstUnderscoreIndex = fullName.indexOf('_');
        const fileCode = firstUnderscoreIndex !== -1 
                      ? fullName.substring(0, firstUnderscoreIndex)
                      : fullName.split('.')[0];
        
        return {
            key: tabKey,
            label: fileCode,
            children: tabKey === activeFileIndex ? (
              <div className={styles.tabContent}>
                <div className={styles.fileInfo}>
                  <div className={styles.fileInfoLeft}>
                    <span className={styles.fileLabel}>File:</span>
                    <span className={styles.fileName}>{fileCode}</span>
                    {/* Assuming record_count is available in file details */}
                    <span className={styles.recordCount}>{pagination.total} Records</span>
                  </div>
                  <Space size="small">
                    <Button 
                        onClick={() => handleDownload(batchData.process_type, fullName)}
                        icon={<DownloadOutlined />} 
                        size="small"
                        className={styles.actionButton}
                    >
                      Download
                    </Button>
                  </Space>
                </div>

                <div className={styles.tableWrapper}>
                  <Spin spinning={loadingRecords} tip="Loading Records...">
                    <Table
                      columns={tableColumns}
                      dataSource={records}
                      // Pagination is handled by the Pagination component below the table, 
                      // or integrated directly if preferred, but manual control offers more flexibility.
                      pagination={false} 
                      scroll={{ x: 'max-content' }} 
                      size="small"
                      className={styles.customAntdTable}
                    />
                    
                    {/* Ant Design Pagination Component */}
                    <div className={styles.paginationControls}>
                        <span className={styles.paginationText}>
                            Showing {startEntry} - {endEntry} of {pagination.total} Entries
                        </span>
                        
                        <div className={styles.paginationButtons}>
                            <button 
                                onClick={() => handlePageChange(pagination.current - 1)}
                                disabled={pagination.current === 1}
                                className={styles.pageButton}
                            >
                                {'<'} {/* Left Arrow */}
                            </button>
                            
                            <span className={styles.pageNumber}>
                                Page {pagination.current} / {totalPages}
                            </span>
                            
                            <button 
                                onClick={() => handlePageChange(pagination.current + 1)}
                                disabled={pagination.current >= totalPages}
                                className={styles.pageButton}
                            >
                                {'>'} {/* Right Arrow */}
                            </button>
                        </div>
                    </div>
                  </Spin>
                </div>
              </div>
            ) : null,
        };
    });
  }, [batchData, activeFileIndex, loadingRecords, records, tableColumns, pagination, handlePageChange]);


  // --- Render Logic ---

  if (loadingBatch) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
            <Spin size="large" tip={`Loading records details for ${uploadRequestId}...`} />
        </div>
      </div>
    );
  }

  if (!batchData) {
      return <div className={styles.container}><div className={styles.error}>Batch details not found.</div></div>;
  }
  
  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return styles.statusCompleted;
      case 'processing': return styles.statusProcessing;
      case 'failed': return styles.statusFailed;
      default: return '';
    }
  };

  const formatDisplayDate = (epochSeconds) => {
    // Check for null, undefined, or zero
    if (!epochSeconds) {
        return '-';
    }
    
    try {
        // 1. Convert seconds (int) to milliseconds (required by JS Date constructor)
        const milliseconds = epochSeconds * 1000; 
        
        const date = new Date(milliseconds);

        // Check if the date is valid (can happen if the epoch value is corrupted)
        if (isNaN(date.getTime())) {
            return 'Invalid Date'; 
        }

        // Get day, month, and year with padding
        const day = String(date.getDate()).padStart(2, '0');
        // Month is 0-indexed, so add 1
        const month = String(date.getMonth() + 1).padStart(2, '0'); 
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    } catch (e) {
        console.error("Date formatting failed:", e);
        return '-';
    }
  };


  return (
    <div className={styles.container}>
      <div className={styles.backSection}>
        <Button onClick={handleBack} type="text" className={styles.backButton} icon={<ArrowLeftOutlined />}>
          Back to Files
        </Button>
      </div>

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>{batchData.process_type || "File Records"}</h1>
          <span className={`${styles.statusBadge} ${getStatusClass(batchData.status)}`}>
            {batchData.status ? batchData.status.charAt(0).toUpperCase() + batchData.status.slice(1) : 'Unknown'}
          </span>
        </div>
        <div className={styles.metaInfo}>
          Upload Request ID - {batchData.upload_request_id} • Uploaded by <b>{batchData.uploaded_by_name || 'N/A'}</b> on {formatDisplayDate(batchData.created_at) || 'N/A'}
        </div>
      </div>
      
      {/* Ant Design Tabs */}
      {batchData.files && batchData.files.length > 0 ? (
        <Tabs 
          activeKey={activeFileIndex} 
          onChange={handleTabChange} 
          items={tabItems}
          type="card"
          className={styles.tabs}
        />
      ) : (
          <div className={styles.noFileMessage}>No files found for this batch.</div>
      )}
    </div>
  );
};

export default RecordDetails;